import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/*
SPDX-FileCopyrightText: 2024 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/
import { Button } from "rsuite";
import { RSForm, Toaster, Message } from "../index";
import { useState, useEffect, useRef } from "react";
import { CountrySelect, FormTextField, SingleSelectCustomOption, SingleSelect, MultipleSelect, AutoComplete, Dropzone, FormCheckboxes, } from "./index";
const MISSING_DATA_ERROR = "Please complete all required fields before submitting.";
const UNSUPPORTED_FIELD_TYPE = "Unsupported field type:";
function FormAllInOne(props) {
    const { formConfig, initialData, fluid, model, onValidate } = props;
    const [formData, setFormData] = useState({});
    const [modifiedFields, setModifiedFields] = useState({});
    const [formId, _] = useState(() => crypto.randomUUID());
    const hasUnsavedChanges = useRef(false);
    const formRef = useRef(null);
    const toaster = Toaster();
    useEffect(() => {
        if (initialData) {
            setInitialData(initialData);
        }
    }, []);
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges.current) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);
    const pushErrorMessage = (message) => {
        toaster.push(_jsx(Message, { children: message, type: "error", showIcon: true }), {
            duration: 4000,
        });
    };
    useEffect(() => {
        const hasChanges = modifiedFields && Object.keys(modifiedFields).length > 0;
        hasUnsavedChanges.current = hasChanges;
        if (props.onUnsavedChanges) {
            props.onUnsavedChanges(hasChanges);
        }
    }, [modifiedFields, props.onUnsavedChanges]);
    const validateForm = () => {
        if (!formRef.current.check()) {
            pushErrorMessage(MISSING_DATA_ERROR);
            return false;
        }
        else {
            if (props.onSubmit) {
                props.onSubmit(formData, true);
            }
            return true;
        }
    };
    const handleInputChange = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
        setModifiedFields((prev) => ({ ...prev, [name]: value }));
    };
    const setInitialData = (data) => {
        setFormData(() => {
            const initialData = {};
            formConfig.fields.forEach((field) => {
                if (field.type === "checkbox" && field.defaultChecked) {
                    initialData[field.name] = field.defaultChecked;
                }
                else {
                    initialData[field.name] = data[field.name] || "";
                }
            });
            return initialData;
        });
    };
    const renderField = (field) => {
        if (!field) {
            return null;
        }
        switch (field.type.toLowerCase()) {
            case "text":
            case "email":
            case "password":
                return (_jsx(FormTextField, { id: formId, name: field.name, label: field.label, accepter: field.accepter, helpText: field.helpText, placeholder: field.placeholder, value: formData[field.name] ?? "", onChange: (value) => handleInputChange(field.name, value), type: field.type, readOnly: field.readOnly, centered: field.centered }));
            case "countryselect":
                return (_jsx(CountrySelect, { label: field.label, value: formData[field.name] ?? "", setValue: (value) => handleInputChange(field.name, value) }));
            case "singleselect":
                return (_jsxs(RSForm.Group, { controlId: `${formId}-${field.name}`, children: [_jsx(RSForm.ControlLabel, { children: field.label }), _jsx(SingleSelect, { data: field.data, placeholder: field.placeholder, value: formData[field.name] ?? "", setValue: (value) => handleInputChange(field.name, value), block: field.block })] }));
            case "singleselectcustomoption":
                return (_jsx(SingleSelectCustomOption, { id: formId, value: formData[field.name] ?? "", setValue: (value) => handleInputChange(field.name, value), data: field.data, label: field.label, customOptionPlaceholder: field.customOptionPlaceholder }));
            case "dropzone":
                return (_jsx(Dropzone, { endpoint: field.endpoint, fileType: field.fileType, generateMessages: field.generateMessages, setResponse: field.setResponse }));
            case "autocomplete":
                return (_jsx(AutoComplete, { label: field.label, data: field.data, value: formData[field.name] ?? "", onChange: (value) => handleInputChange(field.name, value) }));
            case "multipleselect":
                return (_jsx(MultipleSelect, { block: field.block, data: field.data, label: field.label, value: formData[field.name] || [], setValue: (value) => handleInputChange(field.name, value), placeholder: field.placeholder, disabled: field.disabled, loading: field.loading, open: field.open, onOpen: field.onOpen, onEntering: field.onEntering, onClose: field.onClose, onClick: field.onClick, renderMenuItem: field.renderMenuItem, renderValue: field.renderValue, noSearch: field.noSearch }));
            case "checkbox":
                return (_jsx(FormCheckboxes, { id: `${formId}-${field.name}-checkbox`, label: field.label, checkboxConfig: field.checkboxConfig, checkedItems: formData[field.name] ?? [], setCheckedItems: (value) => handleInputChange(field.name, value), hidden: field.hidden, inline: field.inline, indeterminate: field.indeterminate, defaultChecked: field.defaultChecked }));
            default:
                console.warn(`${UNSUPPORTED_FIELD_TYPE} ${field.type}`);
                return null;
        }
    };
    return (_jsx("div", { className: "form-wrapper", children: _jsxs(RSForm, { fluid: fluid, ref: formRef, id: `form-${formId}`, onSubmit: validateForm, model: model || null, formValue: formData, children: [formConfig.fields.map((field) => (_jsx("div", { children: renderField(field) }, `${formId}-${field.name}`))), formConfig.buttonConfig && (_jsx("div", { style: formConfig.buttonConfig.buttonStyle, children: formConfig.buttonConfig.buttons.map((button, index) => (_jsx(Button, { children: button.text, appearance: button.appearance, color: button.color, active: button.active, block: button.block, disabled: button.disabled, loading: button.loading, endIcon: button.endIcon, startIcon: button.startIcon, type: button.type, onClick: () => {
                            if (modifiedFields && onValidate) {
                                onValidate(validateForm());
                            }
                            setModifiedFields({});
                            button.onClick(formData);
                        } }, `form-${formId}-button-${index}`))) }))] }) }));
}
export default FormAllInOne;
